import { FormDrawer } from "../../components/FormDrawer";
import { useFormDirty } from "../../../../../../shared/frontend/forms/useFormDirty";
import { DownloadOutlined, SearchOutlined, UserOutlined } from "@ant-design/icons";
import { Alert, Button, Flex, Form, Input, Select, Space, Tag, Tabs, Typography, ActionMenu } from "../../../../../../shared/frontend/ui";
import { DatePicker } from "../../../../../../shared/frontend/components/ControlledPickers";
import dayjs, { type Dayjs } from "dayjs";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DetailField, DetailList } from "../../../../../../shared/frontend/components/DetailList";
import { useAuth } from "../../app/auth/useAuth";
import { usePersonFileNavigation } from "../../app/navigation/usePersonFileNavigation";
import { Permissions } from "../../app/constants/permissions";
import { formDefaults, spacing } from "../../app/design-system";
import { useI18n } from "../../app/i18n/useI18n";
import { useRealtimeEvent } from "../../app/realtime/RealtimeContext";
import { isBackgroundRefreshPaused } from "../../../../../../shared/frontend/system/backgroundRefresh";
import { useBackgroundRefresh } from "../../../../../../shared/frontend/system/useBackgroundRefresh";
import { RealtimeEventTypes } from "../../app/realtime/realtimeEventTypes";
import { useGlobalStudioContext } from "../../app/studios/useGlobalStudioContext";
import { formatDateOnly, formatDateTimeNoSeconds } from "../../app/units/format";
import { getActiveRequestSort } from "../../app/utils/proTableSortPersistence";
import { decimalSeparatorOf, moneyPlaceholderOf } from "../../app/units/format";
import { useDateInputFormats } from "../../app/units/DateInputFormatContext";
import { loadUnits } from "../../app/units/unitsStorage";
import { isFormValidationError } from "../../app/utils/errorMessage";
import { sanitizeEmailHtml } from "../../app/utils/sanitizeHtml";
import { useSubmitLock } from "../../../../../../shared/frontend/components/hooks/useSubmitLock";
import {
  latestAllowedRestPeriodDate,
  preferredRestPeriodContractId,
  sortRestPeriodContracts,
} from "../../components/contracts/restPeriodContractChoice";
import { RestPeriodFormFields } from "../../components/contracts/RestPeriodFormFields";
import { FormSectionHeading } from "../../components/FormSectionHeading";
import { LoadingOverlay } from "../../components/LoadingOverlay";
import {
  SettingsListPage,
  type ColumnDefinition,
  type ProColumnsWithMeasure,
  type SettingsListActionRef,
} from "../../components/SettingsListPage";
import {
  listActionPreset,
  makeActionColumn,
  ResponsiveFormGrid,
  type ListActionDefinition,
} from "../../components/ui/antd";
import { useFeedback } from "../../../../../../shared/frontend/feedback/useFeedback";
import { useSendDocumentReviewWorkspace } from "../../components/communication/useSendDocumentReviewWorkspace";
import { InboundEmailContractRequestDrawer, type EmailContractAction } from "../../components/contracts/InboundEmailContractRequestDrawer";
import { downloadBlob } from "../../../../../../shared/frontend/utils/fileDownloads";
import { APPOINTMENT_CHANGE_TYPE, ATTACHMENT_EXTRACTION_STATUS, INBOUND_EMAIL_CONTRACT_STATUS, INBOUND_EMAIL_RELEVANCE_VALUE, INBOUND_EMAIL_REVIEW_REASON, INBOUND_EMAIL_UNCLASSIFIED_FILTER_VALUE, MIN_PERSON_SEARCH_LENGTH, PERCENT_SCALE, REALTIME_REFRESH_DELAY_MS, REST_PERIOD_REQUEST_TYPE } from "./inboundEmailValues";

const inboundEmailInboxColumnWidths = {
  processingStatus: 150,
  relevance: 125,
  caseType: 150,
  receivedAt: 175,
  actionsColumn: 210,
} as const;

const inboundEmailInboxActionSlotCounts = {
  actionsColumn: 2,
} as const;

type InboundEmailStatus = "queued" | "processing" | "completed" | "needs_review" | "failed";
type InboundEmailRelevance = "relevant" | "irrelevant" | "uncertain";
type InboundEmailCaseType = "cancellation" | "rest_period" | "appointment_change" | "other_service";
type InboundEmailUnclassifiedFilterValue = typeof INBOUND_EMAIL_UNCLASSIFIED_FILTER_VALUE;
type EmailHeaderAction = EmailContractAction | Extract<InboundEmailCaseType, "appointment_change">;
const INBOUND_EMAIL_REANALYZE_ACTION = "reanalyze";

type InboundEmailListItem = {
  id: string;
  accountId: string;
  accountName?: string | null;
  studioId?: string | null;
  studioName?: string | null;
  fromEmail: string;
  fromDisplayName?: string | null;
  subject?: string | null;
  receivedAt: string;
  processingStatus: InboundEmailStatus;
  effectiveRelevance?: InboundEmailRelevance | null;
  effectiveCaseType?: InboundEmailCaseType | null;
  appointmentChangeType?: string | null;
  confidence?: number | null;
  personId?: string | null;
  personDisplayName?: string | null;
  contractPackageId?: string | null;
  contractPackageDisplayName?: string | null;
  reviewReason?: string | null;
  hasAttachments: boolean;
  hasManualCorrection: boolean;
  updatedAt?: string | null;
};

type InboundEmailAttachment = {
  id: string;
  fileName: string;
  contentType?: string | null;
  sizeBytes?: number | null;
  extractionStatus?: string | null;
  errorReason?: string | null;
};

type InboundEmailPersonCandidate = {
  candidateId: string;
  id?: string | null;
  displayName: string;
  reason?: string | null;
  matchFactors?: string[] | null;
  matchScore?: number | null;
  aiSelected?: boolean;
  effectiveSelected?: boolean;
};

type InboundEmailContractCandidate = InboundEmailPersonCandidate & {
  status?: string | null;
  startDate?: string | null;
  currentEndDate?: string | null;
  term?: string | null;
  renewal?: string | null;
  cancellationModel?: string | null;
  allowsRestPeriods?: boolean;
  cancellationRequestedDate?: string | null;
  cancellationEffectiveDate?: string | null;
  selectionScore?: number | null;
  selectionFactors?: string[];
  selectionPreferenceCount?: number;
};

type InboundEmailClassification = {
  relevance?: InboundEmailRelevance | null;
  caseType?: InboundEmailCaseType | null;
  intentTypes?: InboundEmailCaseType[];
  intents?: InboundEmailIntentSnapshot[];
  confidence?: number | null;
  summary?: string | null;
  reason?: string | null;
  extractedFields?: InboundEmailExtractedField[];
  evidence?: string[];
  currentMessageEvidence?: string[];
  historyEvidence?: string[];
  uncertainties?: string[];
  personReference?: string | null;
  contractSelectionReason?: string | null;
  personId?: string | null;
  personDisplayName?: string | null;
  contractPackageId?: string | null;
  contractPackageDisplayName?: string | null;
};

type InboundEmailAppointmentCandidate = {
  id: string;
  appointmentType?: string | null;
  startTime: string;
  endTime: string;
  status: string;
  matchFactors?: string[];
  matchScore?: number;
  selected?: boolean;
};

type InboundEmailIntentSnapshot = {
  type: InboundEmailCaseType;
  confidence?: number | null;
  summary?: string | null;
  evidence?: string | null;
};

type InboundEmailExtractedField = {
  key: string;
  value?: string | null;
  confidence?: number | null;
  evidence?: string | null;
};

type InboundEmailDetail = InboundEmailListItem & {
  contractProcessings?: Array<{ action: EmailContractAction; completedAt: string; contractPackageId: string; restPeriodId?: string | null }>;
  toEmail?: string | null;
  bodyText?: string | null;
  bodyHtml?: string | null;
  originalClassification?: InboundEmailClassification | null;
  effectiveClassification?: InboundEmailClassification | null;
  effectiveIntentTypes?: InboundEmailCaseType[];
  personCandidates?: InboundEmailPersonCandidate[];
  contractCandidates?: InboundEmailContractCandidate[];
  appointmentCandidates?: InboundEmailAppointmentCandidate[];
  attachments?: InboundEmailAttachment[];
  requestedCancellationDate?: string | null;
  calculatedCancellationDate?: string | null;
  cancellationReason?: string | null;
  cancellationReasonId?: string | null;
  note?: string | null;
  restPeriodStart?: string | null;
  restPeriodEnd?: string | null;
  restPeriodReason?: string | null;
  restPeriodReasonId?: string | null;
  restPeriodDraft?: RestPeriodDraft | null;
  modelName?: string | null;
  promptVersion?: string | null;
  processingError?: string | null;
  processingAttempts?: number;
  nextProcessingAttemptAt?: string | null;
  automaticRetryScheduled?: boolean;
  automaticRetriesExhausted?: boolean;
  canReanalyze?: boolean;
};

type InboundEmailListResponse = { total: number; data: InboundEmailListItem[] };
type PersonOption = { id: string; firstName?: string; lastName?: string; email?: string | null };
type ContractOption = {
  id: string;
  contractPlanName?: string | null;
  name?: string | null;
  status?: number | string | null;
  startDate?: string | null;
  primaryContractEndDate?: string | null;
  cancellationEffectiveDate?: string | null;
};

type Filters = {
  relevance?: Array<InboundEmailRelevance | InboundEmailUnclassifiedFilterValue>;
  caseType?: Array<InboundEmailCaseType | InboundEmailUnclassifiedFilterValue>;
  status?: InboundEmailStatus[];
};

type CorrectionValues = {
  relevance?: InboundEmailRelevance;
  caseType?: InboundEmailCaseType;
  intentTypes?: InboundEmailCaseType[];
  personId?: string;
  contractPackageId?: string;
  requestedCancellationDate?: Dayjs | null;
  cancellationReasonId?: string;
  note?: string;
  restPeriodStartDate?: Dayjs | null;
  restPeriodEndDate?: Dayjs | null;
  restPeriodReasonId?: string;
  restPeriodTemplateId?: string | null;
  restPeriodFeeMode?: string | null;
  monthlyFee?: number | null;
  feeAmountUnit?: number | null;
  feeCycleUnit?: number | null;
  feeCycleValue?: number | null;
  weeksCount?: number | null;
  proportionalCalculationMethod?: number | null;
  extendsContract?: boolean | null;
  continuePayment?: boolean | null;
  appendFreeToEnd?: boolean | null;
  billingScheduleMode?: number | null;
  allowCheckInDuringRestPeriod?: boolean | null;
};

type RestPeriodDraft = {
  restPeriodTemplateId?: string | null;
  restPeriodFeeMode?: string | null;
  monthlyFee?: number | null;
  feeAmountUnit?: number | null;
  feeCycleUnit?: number | null;
  feeCycleValue?: number | null;
  weeksCount?: number | null;
  proportionalCalculationMethod?: number | null;
  extendsContract?: boolean | null;
  continuePayment?: boolean | null;
  appendFreeToEnd?: boolean | null;
  billingScheduleMode?: number | null;
  allowCheckInDuringRestPeriod?: boolean | null;
};

type RestPeriodTemplateOption = RestPeriodDraft & { id: string; name: string; isActive?: boolean };

const PAGE_SIZE = 20;
const CORRECTION_FORM_ID = "inbound-email-classification-correction";
type EmailDetailTab = "message" | "result" | "decision";

type PersistedInboxView = {
  filters?: Filters;
};

function inboxFilterStorageKey(accountId: string | null | undefined): string {
  return `communication.inbox.email.filters.${(accountId || "anon").trim() || "anon"}`;
}

function normalizeFilterValues<T extends string>(value: unknown): T[] | undefined {
  const values = Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : typeof value === "string"
      ? [value]
      : [];
  return values.length > 0 ? values as T[] : undefined;
}

function loadInboxView(accountId: string | null | undefined): {
  search: string;
  filters: Filters;
} {
  if (typeof window === "undefined") return { search: "", filters: {} };
  try {
    const raw = window.localStorage.getItem(inboxFilterStorageKey(accountId));
    if (!raw) return { search: "", filters: {} };
    const stored = JSON.parse(raw) as PersistedInboxView;
    return {
      search: "",
      filters: {
        relevance: normalizeFilterValues<InboundEmailRelevance | InboundEmailUnclassifiedFilterValue>(stored.filters?.relevance),
        caseType: normalizeFilterValues<InboundEmailCaseType | InboundEmailUnclassifiedFilterValue>(stored.filters?.caseType),
        status: normalizeFilterValues<InboundEmailStatus>(stored.filters?.status),
      },
    };
  } catch {
    return { search: "", filters: {} };
  }
}

function saveInboxView(
  accountId: string | null | undefined,
  filters: Filters,
): void {
  if (typeof window === "undefined") return;
  const stored: PersistedInboxView = {
    filters,
  };
  try {
    window.localStorage.setItem(inboxFilterStorageKey(accountId), JSON.stringify(stored));
  } catch {
    // Browser storage may be unavailable; the current view remains usable.
  }
}

function relevanceColor(value?: InboundEmailRelevance | null) {
  if (value === "relevant") return "green";
  if (value === INBOUND_EMAIL_RELEVANCE_VALUE.irrelevant) return "default";
  return "gold";
}

function statusColor(value: InboundEmailStatus) {
  if (value === "completed") return "green";
  if (value === "failed") return "red";
  if (value === "needs_review") return "gold";
  if (value === "processing") return "blue";
  return "default";
}

function statusLabel(value: InboundEmailStatus, text: (key: string) => string) {
  if (value === "needs_review") return text("Review required");
  if (value === "processing") return text("Processing");
  if (value === "completed") return text("Completed");
  if (value === "failed") return text("Failed");
  return text("Queued");
}

function relevanceLabel(value: InboundEmailRelevance, text: (key: string) => string) {
  if (value === "relevant") return text("Relevant");
  if (value === "irrelevant") return text("Irrelevant");
  return text("Uncertain");
}

/**
 * Absage, Verschiebung und unklare Änderung sind fachlich eine Anliegensart, aber nicht derselbe
 * Vorgang. Der Sammelbegriff beschreibt eine Absage deshalb falsch; wo die Unterart vorliegt, trägt
 * sie das Etikett. Nur der unklare Fall bleibt „Terminänderung" — dort ist der Sammelbegriff die
 * richtige Aussage.
 */
function caseTypeLabel(
  value: InboundEmailCaseType,
  text: (key: string) => string,
  appointmentChangeType?: string | null,
) {
  if (value === "cancellation") return text("Cancellation");
  if (value === "rest_period") return text("Rest period");
  if (value === "appointment_change") {
    if (appointmentChangeType === APPOINTMENT_CHANGE_TYPE.booking) return text("Appointment booking request");
    if (appointmentChangeType === APPOINTMENT_CHANGE_TYPE.confirmation) return text("Appointment acceptance");
    if (appointmentChangeType === APPOINTMENT_CHANGE_TYPE.cancellation) return text("Appointment cancellation");
    if (appointmentChangeType === APPOINTMENT_CHANGE_TYPE.reschedule) return text("Appointment reschedule");
    return text("Appointment change");
  }
  if (value === "other_service") return text("Other service request");
  return text("—");
}

function appointmentChangeTypeLabel(value: string | null | undefined, text: (key: string) => string) {
  if (value === APPOINTMENT_CHANGE_TYPE.booking) return text("Appointment booking request");
  if (value === APPOINTMENT_CHANGE_TYPE.confirmation) return text("Appointment acceptance");
  if (value === APPOINTMENT_CHANGE_TYPE.cancellation) return text("Cancellation");
  if (value === APPOINTMENT_CHANGE_TYPE.reschedule) return text("Reschedule");
  if (value === APPOINTMENT_CHANGE_TYPE.unclear) return text("Unclear");
  return text("—");
}

function manuallyActionableIntentTypes(values: InboundEmailCaseType[] | undefined): InboundEmailCaseType[] {
  return values ?? [];
}

function intentTypesOf(detail: InboundEmailDetail): InboundEmailCaseType[] {
  const types = detail.effectiveClassification?.intents?.map((intent) => intent.type)
    ?? detail.effectiveIntentTypes
    ?? detail.effectiveClassification?.intentTypes
    ?? [];
  if (types.length > 0) return [...new Set(types)];
  const caseType = detail.effectiveCaseType ?? detail.effectiveClassification?.caseType;
  return caseType ? [caseType] : [];
}

function classifiedIntentsOf(detail: InboundEmailDetail): InboundEmailIntentSnapshot[] {
  const intents = detail.effectiveClassification?.intents ?? [];
  if (intents.length > 0) return intents;

  return intentTypesOf(detail).map((type, index) => ({
    type,
    confidence: index === 0 ? detail.effectiveClassification?.confidence : null,
    summary: index === 0 ? detail.effectiveClassification?.summary : null,
  }));
}

function hasContractIntent(detail: InboundEmailDetail): boolean {
  return intentTypesOf(detail).some((intent) => intent === "cancellation" || intent === "rest_period");
}

function extractedFieldOf(
  detail: InboundEmailDetail,
  key: string,
): InboundEmailExtractedField | undefined {
  return detail.effectiveClassification?.extractedFields?.find((field) => field.key === key);
}

function classificationReasonFieldOf(detail: InboundEmailDetail): InboundEmailExtractedField | undefined {
  return extractedFieldOf(detail, "classification_reason");
}

function personReferenceFieldOf(detail: InboundEmailDetail): InboundEmailExtractedField | undefined {
  const directReference = extractedFieldOf(detail, "sender_name")
    ?? extractedFieldOf(detail, "person_reference");
  if (directReference) return directReference;

  const firstName = extractedFieldOf(detail, "first_name");
  const lastName = extractedFieldOf(detail, "last_name");
  const name = [firstName?.value, lastName?.value].filter(Boolean).join(" ");
  if (!name) return undefined;

  const confidences = [firstName?.confidence, lastName?.confidence]
    .filter((value): value is number => value != null);
  return {
    key: "person_reference",
    value: name,
    confidence: confidences.length > 0 ? Math.min(...confidences) : null,
    evidence: [firstName?.evidence, lastName?.evidence].filter(Boolean).join(" "),
  };
}

function hasExtractedInformation(detail: InboundEmailDetail): boolean {
  return Boolean(
    personReferenceFieldOf(detail) ||
    extractedFieldOf(detail, "date_of_birth") ||
    extractedFieldOf(detail, "zip") ||
    extractedFieldOf(detail, "city") ||
    extractedFieldOf(detail, "phone") ||
    extractedFieldOf(detail, "cancellation_reason") ||
    extractedFieldOf(detail, "rest_period_reason") ||
    extractedFieldOf(detail, "rest_period_request_type") ||
    extractedFieldOf(detail, "appointment_change_type") ||
    extractedFieldOf(detail, "appointment_date") ||
    detail.requestedCancellationDate ||
    detail.restPeriodStart ||
    detail.restPeriodEnd,
  );
}

function selectedPersonCandidateOf(
  detail: InboundEmailDetail,
): InboundEmailPersonCandidate | undefined {
  const personId = detail.effectiveClassification?.personId ?? detail.personId;
  return detail.personCandidates?.find((candidate) =>
    candidate.effectiveSelected || (personId != null && candidate.id === personId));
}

function selectedContractCandidateOf(
  detail: InboundEmailDetail,
): InboundEmailContractCandidate | undefined {
  const contractPackageId =
    detail.effectiveClassification?.contractPackageId ?? detail.contractPackageId;
  return detail.contractCandidates?.find((candidate) =>
    candidate.effectiveSelected || (contractPackageId != null && candidate.id === contractPackageId));
}

function restPeriodRequestTypeLabel(value: string | null | undefined, text: (key: string) => string) {
  if (value === REST_PERIOD_REQUEST_TYPE.extension) return text("Rest period extension");
  if (value === REST_PERIOD_REQUEST_TYPE.new) return text("New rest period");
  return value || text("—");
}

function confidenceLabel(
  value: number | null | undefined,
  label: string,
  text: (key: string) => string,
) {
  return `${label}: ${value == null ? text("Not assessed") : `${Math.round(value * PERCENT_SCALE)} %`}`;
}

function listPatchOf(detail: InboundEmailDetail): Partial<InboundEmailListItem> {
  return {
    processingStatus: detail.processingStatus,
    effectiveRelevance: detail.effectiveRelevance ?? detail.effectiveClassification?.relevance ?? null,
    effectiveCaseType: detail.effectiveCaseType ?? detail.effectiveClassification?.caseType ?? null,
    appointmentChangeType: extractedFieldOf(detail, "appointment_change_type")?.value ?? null,
    confidence: detail.effectiveClassification?.confidence ?? detail.confidence ?? null,
    personId: detail.personId ?? detail.effectiveClassification?.personId ?? null,
    personDisplayName:
      detail.personDisplayName ?? detail.effectiveClassification?.personDisplayName ?? null,
    contractPackageId:
      detail.contractPackageId ?? detail.effectiveClassification?.contractPackageId ?? null,
    contractPackageDisplayName:
      detail.contractPackageDisplayName
      ?? detail.effectiveClassification?.contractPackageDisplayName
      ?? null,
    reviewReason: detail.reviewReason ?? null,
    hasAttachments: detail.hasAttachments,
    hasManualCorrection: detail.hasManualCorrection,
    updatedAt: detail.updatedAt ?? null,
  };
}

function applyLivePatch(
  row: InboundEmailListItem,
  patch: Partial<InboundEmailListItem> | undefined,
): InboundEmailListItem {
  if (!patch) return row;
  if (row.updatedAt && patch.updatedAt) {
    const rowUpdatedAt = Date.parse(row.updatedAt);
    const patchUpdatedAt = Date.parse(patch.updatedAt);
    if (Number.isFinite(rowUpdatedAt) && Number.isFinite(patchUpdatedAt) && rowUpdatedAt > patchUpdatedAt) {
      return row;
    }
  }
  return { ...row, ...patch };
}

function contractStatusLabel(value: string | null | undefined, text: (key: string) => string) {
  if (value === INBOUND_EMAIL_CONTRACT_STATUS.active) return text("Active");
  if (value === INBOUND_EMAIL_CONTRACT_STATUS.paused) return text("Paused");
  if (value === INBOUND_EMAIL_CONTRACT_STATUS.cancelled) return text("Cancelled");
  if (value === INBOUND_EMAIL_CONTRACT_STATUS.ended) return text("Ended");
  return value || text("—");
}

function extractionStatusLabel(value: string | null | undefined, text: (key: string) => string) {
  const normalized = (value ?? "").trim().toLowerCase();
  if (!normalized) return text("—");
  if (normalized === ATTACHMENT_EXTRACTION_STATUS.queued) return text("Queued");
  if (normalized === ATTACHMENT_EXTRACTION_STATUS.processing) return text("Processing");
  if (normalized === ATTACHMENT_EXTRACTION_STATUS.completed || normalized === ATTACHMENT_EXTRACTION_STATUS.extracted) return text("Text extracted");
  if (normalized === ATTACHMENT_EXTRACTION_STATUS.pending) return text("Not analyzed");
  if (normalized === ATTACHMENT_EXTRACTION_STATUS.unsupported) return text("Unsupported attachment");
  if (normalized === ATTACHMENT_EXTRACTION_STATUS.failed) return text("Extraction failed");
  return text("Review required");
}

function reviewReasonLabel(value: string | null | undefined, text: (key: string) => string) {
  const normalized = (value ?? "").trim().toLowerCase();
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.modelNotConfigured) return text("The analysis model is not configured.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.invalidModelOutput) return text("The classification could not be created because of an invalid AI response.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.modelOutputTruncated)
    return text("The analysis answer was cut off at the token limit.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.modelProviderTimeout)
    return text("The analysis model did not respond in time.");
  if (normalized.startsWith("model_")) return text("The analysis model is currently unavailable.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.attachmentNeedsReview) return text("At least one attachment requires manual review.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.personNotFound) return text("No person could be matched to this message.");
  // Mehrdeutigkeit entsteht sowohl über die Absenderadresse als auch über die Merkmale im
  // Schreiben; der Text nennt deshalb keine Quelle mehr.
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.personAmbiguous) return text("More than one person matches this message.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.contractNotFound) return text("No active contract could be assigned.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.contractAmbiguous) return text("The contract selection requires manual review.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.otherServiceNeedsReview) return text("This request requires a manual response.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.classificationUncertain) return text("The classification is uncertain.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.analysisFailed) return text("The analysis failed because of a system error.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.attachmentTooLarge) return text("An attachment is too large for automatic analysis.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.messageTooLarge || normalized === INBOUND_EMAIL_REVIEW_REASON.rawMessageTooLarge)
    return text("The message is too large for automatic analysis.");
  // Die Anlagen-Codes kommen mit Präfix aus InboundEmailAttachmentExtractionErrorCodes; die
  // präfixlosen Varianten bleiben für ältere Datensätze erhalten.
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.formatUnsupported || normalized === INBOUND_EMAIL_REVIEW_REASON.attachmentFormatUnsupported)
    return text("The attachment format is not supported.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.ocrUnavailable || normalized === INBOUND_EMAIL_REVIEW_REASON.attachmentOcrUnavailable)
    return text("Text recognition is currently unavailable.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.corrupt || normalized === INBOUND_EMAIL_REVIEW_REASON.attachmentCorrupt)
    return text("The attachment is damaged and could not be read.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.attachmentOcrFailed)
    return text("Text recognition of the attachment failed.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.attachmentTextEmpty)
    return text("No readable text was found in the attachment.");
  if (normalized === INBOUND_EMAIL_REVIEW_REASON.attachmentExtractedTextTooLarge)
    return text("The text extracted from the attachment is too large.");
  return text("The message requires manual review.");
}

/**
 * Der Personenbezug, den das Modell im Schreiben gefunden hat (Name, Mitgliedsnummer). Wird
 * gebraucht, wenn die Absenderadresse zu keiner Person passt: dann ist das die einzige Spur,
 * nach der im Personen-Feld gesucht werden kann.
 */
function personReferenceOf(detail: InboundEmailDetail): string {
  return (
    detail.effectiveClassification?.personReference ??
    detail.originalClassification?.personReference ??
    ""
  ).trim();
}

function personLabel(person: PersonOption, fallback: string) {
  const name = [person.firstName, person.lastName].filter(Boolean).join(" ").trim();
  return person.email ? `${name || person.email} · ${person.email}` : name || fallback;
}

function personMatchFactorLabel(value: string, text: (key: string) => string): string {
  const keys: Record<string, string> = {
    sender_email: "Sender address",
    last_name: "Last name",
    first_name: "First name",
    date_of_birth: "Date of birth",
    phone: "Phone number",
    zip: "Postal code",
    city: "City",
  };
  return keys[value] ? text(keys[value]) : value;
}

function contractSelectionFactorLabel(value: string, text: (key: string) => string): string {
  const keys: Record<string, string> = {
    contract_current: "Contract is active",
    contract_already_ended_or_cancelled: "Contract already ended or cancelled",
    rest_period_allowed: "Rest periods are allowed",
    rest_period_not_allowed: "Rest periods are not allowed",
    fixed_term_contract: "Fixed-term contract",
    no_fixed_term: "No fixed term",
    requested_date_matches_contract_end: "Requested date matches contract end",
    requested_date_near_contract_end: "Requested date is near contract end",
    requested_date_close_to_contract_end: "Requested date is close to contract end",
    historical_selection_preference: "Suggested from previous assignments",
  };
  return text(keys[value] ?? value);
}

export function InboundEmailInboxTab() {
  const accountId = useAuth().info?.accountId ?? "anonymous";
  return <InboundEmailInboxTabContent key={accountId} />;
}

function InboundEmailInboxTabContent() {
  const [processingAction, setProcessingAction] = React.useState<EmailContractAction | null>(null);
  const auth = useAuth();
  const { text } = useI18n();
  const feedback = useFeedback();
  const openPersonFile = usePersonFileNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const { studioIdsQueryParam } = useGlobalStudioContext();
  const units = React.useMemo(() => loadUnits(auth.info?.accountId), [auth.info?.accountId]);
  const decimalSeparator = React.useMemo(() => decimalSeparatorOf(units), [units]);
  const moneyPlaceholder = React.useMemo(() => moneyPlaceholderOf(units), [units]);
  const currencyCode = (units.currency || "").trim() || "EUR";
  const dateInputFormats = useDateInputFormats();
  const actionRef = React.useRef<SettingsListActionRef>(null);
  const { openDocumentReview, documentReviewWorkspace } = useSendDocumentReviewWorkspace();
  const [initialInboxView] = React.useState(() => loadInboxView(auth.info?.accountId));
  const [searchInput, setSearchInput] = React.useState(initialInboxView.search);
  const [search, setSearch] = React.useState(initialInboxView.search);
  const [filters, setFilters] = React.useState<Filters>(initialInboxView.filters);
  const [personOptions, setPersonOptions] = React.useState<Array<{ value: string; label: string }>>([]);
  const [contractOptions, setContractOptions] = React.useState<Array<{ value: string; label: string }>>([]);
  const [contractChoices, setContractChoices] = React.useState<ContractOption[]>([]);
  const [cancellationReasonOptions, setCancellationReasonOptions] = React.useState<Array<{ value: string; label: string }>>([]);
  const [restPeriodReasonOptions, setRestPeriodReasonOptions] = React.useState<Array<{ value: string; label: string }>>([]);
  const [restPeriodTemplates, setRestPeriodTemplates] = React.useState<RestPeriodTemplateOption[]>([]);
  const [detailId, setDetailId] = React.useState<string | null>(null);
  const [detail, setDetail] = React.useState<InboundEmailDetail | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailLoadFailed, setDetailLoadFailed] = React.useState(false);
  const [detailTab, setDetailTab] = React.useState<EmailDetailTab>("result");
  const [correctionSaving, setCorrectionSaving] = React.useState(false);
  const [reanalyzing, setReanalyzing] = React.useState(false);
  const [liveRowPatches, setLiveRowPatches] = React.useState<
    Record<string, Partial<InboundEmailListItem>>
  >({});
  const [correctionForm] = Form.useForm<CorrectionValues>();
  const { dirty: correctionDirty, hasChanges: hasCorrectionChanges, resetDirty: resetCorrectionDirty } = useFormDirty(correctionForm);
  const correctionPersonId = Form.useWatch("personId", correctionForm);
  const correctionRelevance = Form.useWatch("relevance", correctionForm);
  const watchedCorrectionIntentTypes = Form.useWatch("intentTypes", correctionForm);
  const correctionIntentTypes = React.useMemo(
    () => watchedCorrectionIntentTypes ?? [],
    [watchedCorrectionIntentTypes],
  );
  const correctionRestPeriodTemplateId = Form.useWatch("restPeriodTemplateId", correctionForm);
  const watchedContractPackageId = Form.useWatch("contractPackageId", correctionForm);
  const watchedRestPeriodContract = contractChoices.find((item) => item.id === watchedContractPackageId);
  const latestRestPeriodDate = watchedRestPeriodContract
    ? latestAllowedRestPeriodDate(watchedRestPeriodContract)
    : null;
  const disableFutureRestPeriodDate = React.useCallback((current: Dayjs | null) => {
    if (!current || !latestRestPeriodDate) return false;
    return current.format("YYYY-MM-DD") > latestRestPeriodDate;
  }, [latestRestPeriodDate]);
  const correctionOverridesRef = React.useRef(new Map<string, Partial<InboundEmailListItem>>());
  const visibleRowIdsRef = React.useRef(new Set<string>());
  const detailIdRef = React.useRef<string | null>(detailId);
  const realtimeUpdateTimersRef = React.useRef(new Map<string, number>());

  const canReview = auth.hasAnyPermission(Permissions.CommunicationInboxReview, Permissions.All);
  const { submitBlocked: correctionSubmitBlocked } = useSubmitLock({
    open: detailId !== null && canReview,
    loading: correctionSaving,
    formId: CORRECTION_FORM_ID,
    submitVisible: canReview,
  });

  React.useEffect(() => {
    saveInboxView(
      auth.info?.accountId,
      filters,
    );
  }, [
    auth.info?.accountId,
    filters,
  ]);

  React.useEffect(() => {
    detailIdRef.current = detailId;
  }, [detailId]);

  const syncCorrectionForm = React.useCallback((next: InboundEmailDetail) => {
    correctionForm.resetFields();
    correctionForm.setFieldsValue({
      relevance: next.effectiveRelevance ?? next.effectiveClassification?.relevance ?? "uncertain",
      caseType: next.effectiveCaseType ?? next.effectiveClassification?.caseType ?? undefined,
      intentTypes: manuallyActionableIntentTypes(
        next.effectiveIntentTypes ?? next.effectiveClassification?.intentTypes,
      ),
      personId: next.effectiveClassification?.personId ?? next.personId ?? undefined,
      contractPackageId:
        next.effectiveClassification?.contractPackageId ?? next.contractPackageId ?? undefined,
      requestedCancellationDate:
        next.requestedCancellationDate ? dayjs(next.requestedCancellationDate) : null,
      cancellationReasonId: next.cancellationReasonId ?? undefined,
      note: next.note ?? undefined,
      restPeriodStartDate: next.restPeriodStart ? dayjs(next.restPeriodStart) : null,
      restPeriodEndDate: next.restPeriodEnd ? dayjs(next.restPeriodEnd) : null,
      restPeriodReasonId: next.restPeriodReasonId ?? undefined,
      ...(next.restPeriodDraft ?? {}),
    });
    resetCorrectionDirty();
  }, [correctionForm, resetCorrectionDirty]);

  const applyDetail = React.useCallback((
    next: InboundEmailDetail,
    options?: { syncForm?: boolean },
  ) => {
    setDetail(next);
    if (next.personId && next.personDisplayName) {
      setPersonOptions((current) => current.some((item) => item.value === next.personId)
        ? current
        : [{ value: next.personId!, label: next.personDisplayName! }, ...current]);
    }
    if (next.personCandidates?.length) {
      setPersonOptions((current) => {
        const known = new Set(current.map((item) => item.value));
        return [
          ...current,
          ...next.personCandidates!
            .filter((candidate) => candidate.id && !known.has(candidate.id))
            .map((candidate) => ({ value: candidate.id!, label: candidate.displayName })),
        ];
      });
    }
    if (next.contractPackageId && next.contractPackageDisplayName) {
      setContractOptions((current) => current.some((item) => item.value === next.contractPackageId)
        ? current
        : [{ value: next.contractPackageId!, label: next.contractPackageDisplayName! }, ...current]);
    }
    if (next.contractCandidates?.length) {
      setContractOptions((current) => {
        const known = new Set(current.map((item) => item.value));
        return [
          ...current,
          ...next.contractCandidates!
            .filter((candidate) => candidate.id && !known.has(candidate.id))
            .map((candidate) => ({ value: candidate.id!, label: candidate.displayName })),
        ];
      });
    }
    if (options?.syncForm !== false) syncCorrectionForm(next);
  }, [syncCorrectionForm]);

  const loadDetail = React.useCallback(async (id: string) => {
    setDetailLoading(true);
    setDetailLoadFailed(false);
    try {
      const next = await auth.api.get<InboundEmailDetail>(`/api/v1/inbound-emails/${encodeURIComponent(id)}`);
      applyDetail(next);
    } catch {
      setDetailLoadFailed(true);
    } finally {
      setDetailLoading(false);
    }
  }, [applyDetail, auth.api]);

  const openDetail = React.useCallback((row: InboundEmailListItem, tab: EmailDetailTab = "result") => {
    setDetailTab(tab);
    setDetailId(row.id);
    setDetail(null);
    void loadDetail(row.id);
  }, [loadDetail]);

  const requestedEmailId = new URLSearchParams(location.search).get("emailId");
  React.useEffect(() => {
    if (!requestedEmailId) return;
    let alive = true;
    void auth.api.get<InboundEmailDetail>(`/api/v1/inbound-emails/${encodeURIComponent(requestedEmailId)}`)
      .then(next => {
        if (!alive) return;
        setDetailId(next.id);
        applyDetail(next);
      }).catch(() => {
        if (!alive) return;
        setDetailId(requestedEmailId);
        setDetailLoadFailed(true);
      });
    return () => { alive = false; };
  }, [requestedEmailId, auth.api, applyDetail]);

  const closeDetail = React.useCallback(() => {
    setDetailId(null);
    setDetail(null);
    setDetailLoadFailed(false);
    setDetailTab("result");
    correctionForm.resetFields();
  }, [correctionForm]);

  const refreshEmailDetail = React.useCallback((emailId: string) => {
    void auth.api.get<InboundEmailDetail>(`/api/v1/inbound-emails/${encodeURIComponent(emailId)}`)
      .then((next) => {
        setLiveRowPatches((current) => ({ ...current, [emailId]: listPatchOf(next) }));
        if (detailIdRef.current === emailId) {
          applyDetail(next, { syncForm: !hasCorrectionChanges() });
          setDetailLoadFailed(false);
        }
      })
      .catch(() => { void actionRef.current?.revalidateCurrentQuery(); });
  }, [auth.api, applyDetail, hasCorrectionChanges]);
  const pausedInboxIdsRef = React.useRef(new Set<string | null>());
  const refreshInboxAfterPause = useBackgroundRefresh(() => {
    const changedIds = new Set(pausedInboxIdsRef.current);
    pausedInboxIdsRef.current.clear();
    if (changedIds.has(null)) correctionOverridesRef.current.clear();
    else changedIds.forEach(id => { if (id) correctionOverridesRef.current.delete(id); });
    setLiveRowPatches(current => {
      if (changedIds.has(null)) return {};
      const next = { ...current };
      changedIds.forEach(id => { if (id) delete next[id]; });
      return next;
    });
    void actionRef.current?.revalidateCurrentQuery();
    if (detailIdRef.current && (changedIds.has(null) || changedIds.has(detailIdRef.current))) refreshEmailDetail(detailIdRef.current);
  }, `${auth.info?.tenantId ?? ""}:${auth.info?.accountId ?? ""}:${studioIdsQueryParam}`);

  useRealtimeEvent(RealtimeEventTypes.InboxChanged, (event) => {
    const emailId = event.entityId;
    if (isBackgroundRefreshPaused()) {
      pausedInboxIdsRef.current.add(emailId ?? null);
      refreshInboxAfterPause();
      return;
    }
    if (!emailId) {
      void actionRef.current?.revalidateCurrentQuery();
      return;
    }

    correctionOverridesRef.current.delete(emailId);
    const pendingTimer = realtimeUpdateTimersRef.current.get(emailId);
    if (pendingTimer != null) window.clearTimeout(pendingTimer);
    const timer = window.setTimeout(() => {
      realtimeUpdateTimersRef.current.delete(emailId);
      if (isBackgroundRefreshPaused()) {
        pausedInboxIdsRef.current.add(emailId);
        refreshInboxAfterPause();
        return;
      }
      const affectsVisibleRow = visibleRowIdsRef.current.has(emailId);
      const affectsOpenDetail = detailIdRef.current === emailId;
      if (!affectsVisibleRow && !affectsOpenDetail) {
        void actionRef.current?.revalidateCurrentQuery();
        return;
      }

      refreshEmailDetail(emailId);
    }, REALTIME_REFRESH_DELAY_MS);
    realtimeUpdateTimersRef.current.set(emailId, timer);
  }, { enabled: !!auth.token });

  React.useEffect(() => () => {
    realtimeUpdateTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    realtimeUpdateTimersRef.current.clear();
  }, []);

  React.useEffect(() => {
    if (!correctionIntentTypes.includes("cancellation")) return;
    let cancelled = false;
    void auth.api.get<Array<{ id: string; name: string }>>("/api/v1/cancellation-reasons")
      .then((items) => {
        if (!cancelled) setCancellationReasonOptions((items ?? []).map((item) => ({ value: item.id, label: item.name })));
      })
      .catch(() => {
        if (!cancelled) setCancellationReasonOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [auth.api, correctionIntentTypes]);

  React.useEffect(() => {
    void actionRef.current?.revalidateFromFirstPage();
  }, [filters, search, studioIdsQueryParam]);

  React.useEffect(() => {
    if (!correctionPersonId) return;
    let cancelled = false;
    void auth.api.get<ContractOption[]>(
      `/api/v1/inbound-emails/contract-options?personId=${encodeURIComponent(correctionPersonId)}`,
    ).then((items) => {
      if (cancelled) return;
      const sorted = sortRestPeriodContracts(items ?? []);
      setContractChoices(sorted);
      setContractOptions(sorted.map((item) => ({
        value: item.id,
        label: item.contractPlanName || item.name || text("Contract"),
      })));
      if (!correctionIntentTypes.includes("rest_period")) return;
      const currentId = correctionForm.getFieldValue("contractPackageId") as string | undefined;
      const preferred = preferredRestPeriodContractId(sorted, currentId);
      if (preferred && preferred !== currentId) {
        correctionForm.setFieldValue("contractPackageId", preferred);
      }
    }).catch(() => {
      if (!cancelled) {
        setContractChoices([]);
        setContractOptions([]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [auth.api, correctionForm, correctionIntentTypes, correctionPersonId, text]);

  React.useEffect(() => {
    if (!correctionIntentTypes.includes("rest_period")) return;
    let cancelled = false;
    void auth.api.get<Array<{ id: string; name: string }>>("/api/v1/rest-period-reasons")
      .then((items) => {
        if (cancelled) return;
        const options = (items ?? []).map((item) => ({ value: item.id, label: item.name }));
        setRestPeriodReasonOptions(options);
        if (correctionForm.getFieldValue("restPeriodReasonId") || !detail?.restPeriodReason) return;
        const normalizedReason = detail.restPeriodReason.trim().toLocaleLowerCase();
        const matching = options.find((item) => item.label.trim().toLocaleLowerCase() === normalizedReason);
        if (matching) correctionForm.setFieldValue("restPeriodReasonId", matching.value);
      })
      .catch(() => {
        if (!cancelled) setRestPeriodReasonOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [auth.api, correctionForm, correctionIntentTypes, detail?.restPeriodReason]);

  React.useEffect(() => {
    if (!correctionIntentTypes.includes("rest_period")) return;
    let cancelled = false;
    void auth.api.get<RestPeriodTemplateOption[]>("/api/v1/rest-period-templates")
      .then((items) => {
        if (!cancelled) setRestPeriodTemplates(items ?? []);
      })
      .catch(() => {
        if (!cancelled) setRestPeriodTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [auth.api, correctionIntentTypes]);

  const applyRestPeriodTemplate = React.useCallback((templateId?: string) => {
    const template = restPeriodTemplates.find((item) => item.id === templateId);
    if (!template) return;
    correctionForm.setFieldsValue({
      restPeriodTemplateId: template.id,
      restPeriodFeeMode: template.restPeriodFeeMode ?? undefined,
      monthlyFee: template.monthlyFee ?? null,
      feeAmountUnit: template.feeAmountUnit ?? null,
      feeCycleUnit: template.feeCycleUnit ?? null,
      feeCycleValue: template.feeCycleValue ?? null,
      weeksCount: template.weeksCount ?? null,
      proportionalCalculationMethod: template.proportionalCalculationMethod ?? null,
      extendsContract: template.extendsContract ?? true,
      continuePayment: template.continuePayment ?? false,
      appendFreeToEnd: template.appendFreeToEnd ?? false,
      billingScheduleMode: template.billingScheduleMode ?? 1,
      allowCheckInDuringRestPeriod: template.allowCheckInDuringRestPeriod ?? false,
    });
  }, [correctionForm, restPeriodTemplates]);

  const searchPersons = React.useCallback((value: string) => {
    const query = value.trim();
    if (query.length < MIN_PERSON_SEARCH_LENGTH) return;
    void auth.api.get<PersonOption[]>(`/api/v1/inbound-emails/person-options?q=${encodeURIComponent(query)}`)
      .then((items) => setPersonOptions((items ?? []).map((person) => ({
        value: person.id,
        label: personLabel(person, text("Person")),
      }))))
      .catch(() => setPersonOptions([]));
  }, [auth.api, text]);

  const request = React.useCallback(async (
    params?: { current?: number; pageSize?: number },
    sort?: Record<string, "ascend" | "descend">,
  ) => {
    const current = params?.current ?? 1;
    const pageSize = params?.pageSize ?? PAGE_SIZE;
    const query = new URLSearchParams();
    query.set("skip", String((current - 1) * pageSize));
    query.set("take", String(pageSize));
    if (studioIdsQueryParam) query.set("studioIds", studioIdsQueryParam);
    if (search.trim()) query.set("search", search.trim());
    if (filters.relevance?.length) query.set("relevance", filters.relevance.join(","));
    if (filters.caseType?.length) query.set("caseType", filters.caseType.join(","));
    if (filters.status?.length) query.set("status", filters.status.join(","));
    const activeSort = getActiveRequestSort(sort);
    if (activeSort) {
      query.set("sortBy", activeSort.field);
      query.set("sortOrder", activeSort.direction);
    }
    const result = await auth.api.get<InboundEmailListResponse>(`/api/v1/inbound-emails?${query.toString()}`);
    return {
      data: (result.data ?? []).map((row) => ({ ...row, ...correctionOverridesRef.current.get(row.id) })),
      total: result.total ?? 0,
      success: true,
    };
  }, [auth.api, filters, search, studioIdsQueryParam]);

  const saveCorrection = React.useCallback(async (): Promise<boolean> => {
    if (!detail || !canReview) return true;
    // Bei fehlenden Pflichtangaben lehnt validateFields mit einem Objekt ab. Ohne dieses Abfangen
    // wird daraus eine unbehandelte Rejection und der Nutzer sieht statt der markierten Felder das
    // Absturz-Overlay.
    let values: CorrectionValues;
    try {
      values = await correctionForm.validateFields();
    } catch (error) {
      if (isFormValidationError(error)) return false;
      throw error;
    }
    const previous = detail;
    const optimistic: Partial<InboundEmailListItem> = {
      effectiveRelevance: values.relevance ?? "relevant",
      effectiveCaseType: values.relevance === "irrelevant" ? null : values.intentTypes?.[0],
      personId: values.personId || null,
      contractPackageId: values.contractPackageId || null,
      hasManualCorrection: true,
    };
    correctionOverridesRef.current.set(detail.id, optimistic);
    setLiveRowPatches((current) => ({ ...current, [detail.id]: optimistic }));
    setDetail({ ...detail, ...optimistic });
    setCorrectionSaving(true);
    try {
      const saved = await auth.api.patch<InboundEmailDetail>(
        `/api/v1/inbound-emails/${encodeURIComponent(detail.id)}/classification`,
        {
          relevance: values.relevance ?? "relevant",
          intentTypes: values.relevance === "irrelevant" ? [] : values.intentTypes,
          personId: values.personId || null,
          contractPackageId: values.contractPackageId || null,
          requestedTerminationDate: values.requestedCancellationDate?.format("YYYY-MM-DD") ?? null,
          cancellationReasonId: values.cancellationReasonId || null,
          note: values.note?.trim() || null,
          restPeriodStartDate: values.restPeriodStartDate?.format("YYYY-MM-DD") ?? null,
          restPeriodEndDate: values.restPeriodEndDate?.format("YYYY-MM-DD") ?? null,
          restPeriodReasonId: values.restPeriodReasonId || null,
          restPeriodDraft: values.intentTypes?.includes("rest_period") ? {
            restPeriodTemplateId: values.restPeriodTemplateId || null,
            restPeriodFeeMode: values.restPeriodFeeMode || null,
            monthlyFee: values.monthlyFee ?? null,
            feeAmountUnit: values.feeAmountUnit ?? null,
            feeCycleUnit: values.feeCycleUnit ?? null,
            feeCycleValue: values.feeCycleValue ?? null,
            weeksCount: values.weeksCount ?? null,
            proportionalCalculationMethod: values.proportionalCalculationMethod ?? null,
            extendsContract: values.extendsContract ?? null,
            continuePayment: values.continuePayment ?? null,
            appendFreeToEnd: values.appendFreeToEnd ?? null,
            billingScheduleMode: values.billingScheduleMode ?? null,
            allowCheckInDuringRestPeriod: values.allowCheckInDuringRestPeriod ?? null,
          } : null,
          expectedUpdatedAt: detail.updatedAt ?? null,
        },
      );
      correctionOverridesRef.current.delete(detail.id);
      setLiveRowPatches((current) => ({
        ...current,
        [detail.id]: listPatchOf(saved),
      }));
      applyDetail(saved);
      return true;
    } catch (error) {
      correctionOverridesRef.current.delete(detail.id);
      setLiveRowPatches((current) => ({
        ...current,
        [detail.id]: listPatchOf(previous),
      }));
      setDetail(previous);
      void actionRef.current?.revalidateCurrentQuery();
      feedback.error(error instanceof Error ? error.message : text("Save failed"));
      return false;
    } finally {
      setCorrectionSaving(false);
    }
  }, [applyDetail, auth.api, canReview, correctionForm, detail, feedback, text]);

  const saveAndCloseDetail = React.useCallback(async () => {
    if (correctionSaving) return;
    if (canReview && hasCorrectionChanges()) {
      const saved = await saveCorrection();
      if (!saved) return;
    }
    closeDetail();
  }, [canReview, closeDetail, hasCorrectionChanges, correctionSaving, saveCorrection]);

  const processEmailAction = React.useCallback(async (action: EmailHeaderAction) => {
    if (!detail) return;
    if (canReview && hasCorrectionChanges() && !await saveCorrection()) return;
    if (action === "appointment_change") navigate(`/calendar?emailId=${encodeURIComponent(detail.id)}`, { state: { fromEmailId: detail.id } });
    else setProcessingAction(action);
  }, [canReview, detail, hasCorrectionChanges, navigate, saveCorrection]);

  const reanalyze = React.useCallback(async () => {
    if (!detail || !canReview) return;
    const queuedPatch: Partial<InboundEmailListItem> = {
      processingStatus: "queued",
      effectiveRelevance: null,
      effectiveCaseType: null,
      appointmentChangeType: null,
      confidence: null,
      personId: null,
      personDisplayName: null,
      contractPackageId: null,
      contractPackageDisplayName: null,
      hasManualCorrection: false,
      reviewReason: null,
    };
    correctionOverridesRef.current.set(detail.id, queuedPatch);
    setLiveRowPatches((current) => ({ ...current, [detail.id]: queuedPatch }));
    closeDetail();
    setReanalyzing(true);
    try {
      await auth.api.post<{ updatedAt: string }>(
        `/api/v1/inbound-emails/${encodeURIComponent(detail.id)}/reanalyze`,
        { expectedUpdatedAt: detail.updatedAt ?? null },
      );
    } catch (error) {
      correctionOverridesRef.current.delete(detail.id);
      setLiveRowPatches((current) => {
        const next = { ...current };
        delete next[detail.id];
        return next;
      });
      void actionRef.current?.revalidateCurrentQuery();
      feedback.error(error instanceof Error ? error.message : text("Reanalysis failed"));
    } finally {
      setReanalyzing(false);
    }
  }, [auth.api, canReview, closeDetail, detail, feedback, text]);

  const downloadAttachment = React.useCallback(async (attachment: InboundEmailAttachment) => {
    if (!detailId) return;
    const blob = await auth.api.blob(
      `/api/v1/inbound-emails/${encodeURIComponent(detailId)}/attachments/${encodeURIComponent(attachment.id)}`,
    );
    downloadBlob(blob, attachment.fileName);
  }, [auth.api, detailId]);

  const relevanceOptions = React.useMemo(() => [
    { value: "relevant", label: text("Relevant") },
    { value: "irrelevant", label: text("Irrelevant") },
    { value: "uncertain", label: text("Uncertain") },
    { value: "unclassified", label: text("Not classified") },
  ], [text]);
  const caseTypeOptions = React.useMemo(() => [
    { value: "cancellation", label: text("Cancellation") },
    { value: "rest_period", label: text("Rest period") },
    { value: "appointment_change", label: text("Appointment change") },
    { value: "other_service", label: text("Other service request") },
  ], [text]);
  const manuallyActionableCaseTypeOptions = caseTypeOptions;
  const caseTypeFilterOptions = React.useMemo(() => [
    ...caseTypeOptions,
    { value: "unclassified", label: text("Not classified") },
  ], [caseTypeOptions, text]);
  const statusOptions = React.useMemo(() => [
    { value: "queued", label: text("Queued") },
    { value: "processing", label: text("Processing") },
    { value: "completed", label: text("Completed") },
    { value: "needs_review", label: text("Review required") },
    { value: "failed", label: text("Failed") },
  ], [text]);
  const columnDefinitions: ColumnDefinition[] = React.useMemo(() => [
    { key: "processingStatus", label: "Processing status", defaultVisible: true },
    { key: "relevance", label: "Relevance", defaultVisible: true },
    { key: "caseType", label: "Request type", defaultVisible: true },
    { key: "sender", label: "From", defaultVisible: true },
    { key: "subject", label: "Subject", defaultVisible: true },
    { key: "person", label: "Person", defaultVisible: true },
    { key: "contract", label: "Contract", defaultVisible: false },
    { key: "receivedAt", label: "Received at", defaultVisible: true },
  ], []);

  const liveRowOf = React.useCallback(
    (row: InboundEmailListItem) => applyLivePatch(row, liveRowPatches[row.id]),
    [liveRowPatches],
  );

  const handleVisibleRowsChange = React.useCallback((rows: InboundEmailListItem[]) => {
    visibleRowIdsRef.current = new Set(rows.map((row) => row.id));
  }, []);

  const allColumnsMap: Record<string, ProColumnsWithMeasure<InboundEmailListItem>> = React.useMemo(() => ({
    processingStatus: {
      title: text("Processing status"),
      dataIndex: "processingStatus",
      key: "processingStatus",
      width: inboundEmailInboxColumnWidths.processingStatus,
      sorter: true,
      render: (_, row) => {
        const current = liveRowOf(row);
        return (
          <Tag color={statusColor(current.processingStatus)}>
            {statusLabel(current.processingStatus, text)}
          </Tag>
        );
      },
    },
    relevance: {
      title: text("Relevance"),
      dataIndex: "effectiveRelevance",
      key: "relevance",
      width: inboundEmailInboxColumnWidths.relevance,
      sorter: true,
      render: (_, row) => {
        const current = liveRowOf(row);
        return current.effectiveRelevance
          ? (
              <Tag color={relevanceColor(current.effectiveRelevance)}>
                {relevanceLabel(current.effectiveRelevance, text)}
              </Tag>
            )
          : text("—");
      },
    },
    caseType: {
      title: text("Request type"),
      dataIndex: "effectiveCaseType",
      key: "caseType",
      width: inboundEmailInboxColumnWidths.caseType,
      sorter: true,
      render: (_, row) => {
        const current = liveRowOf(row);
        return current.effectiveCaseType
          ? caseTypeLabel(current.effectiveCaseType, text, current.appointmentChangeType)
          : text("—");
      },
    },
    sender: {
      title: text("From"),
      dataIndex: "fromEmail",
      key: "sender",
      ellipsis: true,
      sorter: true,
      render: (_, row) => row.fromDisplayName
        ? <span>{row.fromDisplayName}<br /><Typography.Text type="secondary">{row.fromEmail}</Typography.Text></span>
        : row.fromEmail,
    },
    subject: {
      title: text("Subject"),
      dataIndex: "subject",
      key: "subject",
      ellipsis: true,
      sorter: true,
      render: (_, row) => row.subject || text("(no subject)"),
    },
    person: {
      title: text("Person"),
      dataIndex: "personDisplayName",
      key: "person",
      ellipsis: true,
      sorter: true,
      render: (_, row) => liveRowOf(row).personDisplayName || text("—"),
    },
    contract: {
      title: text("Contract"),
      dataIndex: "contractPackageDisplayName",
      key: "contract",
      ellipsis: true,
      sorter: true,
      render: (_, row) => liveRowOf(row).contractPackageDisplayName || text("—"),
    },
    receivedAt: {
      title: text("Received at"),
      dataIndex: "receivedAt",
      key: "receivedAt",
      width: inboundEmailInboxColumnWidths.receivedAt,
      sorter: true,
      defaultSortOrder: "descend",
      render: (_, row) => formatDateTimeNoSeconds(row.receivedAt, units),
    },
  }), [liveRowOf, text, units]);

  const actionsColumn = React.useMemo(
    () => makeActionColumn<InboundEmailListItem>({ width: inboundEmailInboxColumnWidths.actionsColumn, slotCount: inboundEmailInboxActionSlotCounts.actionsColumn }),
    [],
  );
  const actions = React.useMemo<ListActionDefinition<InboundEmailListItem>[]>(() => [
    listActionPreset<InboundEmailListItem>("view", {
      label: "Details",
      onClick: (row) => openDetail(row, "result"),
    }),
    {
      key: "person",
      label: "Person",
      icon: <UserOutlined />,
      kind: "link",
      visible: (row) => !!liveRowOf(row).personId,
      onClick: (row) => openPersonFile(liveRowOf(row).personId),
    },
  ], [liveRowOf, openPersonFile, openDetail]);

  const emailHeaderItems: React.ComponentProps<typeof ActionMenu>["items"] = [];
  if (detail) {
    for (const action of intentTypesOf(detail)) {
      if (action === "cancellation" && !auth.hasAnyPermission(Permissions.ContractsCancel)) continue;
      if (action === "rest_period" && !auth.hasAnyPermission(Permissions.RestPeriodsWrite)) continue;
      if (action === "appointment_change" && !(auth.hasAnyPermission(Permissions.AppointmentsRead) && auth.hasAnyPermission(Permissions.AppointmentsWrite))) continue;
      if (action !== "cancellation" && action !== "rest_period" && action !== "appointment_change") continue;
      emailHeaderItems.push({
        key: action,
        label: text(action === "cancellation" ? "Process cancellation" : action === "rest_period" ? "Process rest period" : "Process appointment request"),
        disabled: action === "appointment_change" && (correctionRelevance !== "relevant" || detail.processingStatus === "queued" || detail.processingStatus === "processing"),
      });
    }
    if (canReview && detail.canReanalyze) emailHeaderItems.push({ key: INBOUND_EMAIL_REANALYZE_ACTION, label: text("Analyze again"), disabled: reanalyzing });
  }

  // Ein Schreiben vor dem Versand bearbeiten ersetzt die Liste — dieselbe Arbeitsfläche wie in der Akte.


  return (
    <>{documentReviewWorkspace}<React.Activity mode={documentReviewWorkspace ? "hidden" : "visible"}>
    <>
      {processingAction && detailId ? <InboundEmailContractRequestDrawer key={`${detailId}:${processingAction}`}
        emailId={detailId} action={processingAction} onClose={() => setProcessingAction(null)}
        onSuccess={() => { void loadDetail(detailId); void actionRef.current?.revalidateCurrentQuery(); }}
        onOpenDocumentReview={openDocumentReview} /> : null}
      <SettingsListPage<InboundEmailListItem>
        dataMode="server-query"
        storageKey="communication.inbox.email"
        rowKey="id"
        actionRef={actionRef}
        request={request}
        onVisibleRowsChange={handleVisibleRowsChange}
        columnDefinitions={columnDefinitions}
        allColumnsMap={allColumnsMap}
        onRowClick={(row) => openDetail(row)}
        actionsColumn={actionsColumn}
        actions={actions}
        pagination={{ pageSize: PAGE_SIZE }}
        sectionTitle={null}
        toolbarExtra={(
          <Flex wrap="wrap" gap={spacing.sm} align="center" style={{ width: "100%", minWidth: 0 }}>
            <Input.Search
              allowClear
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onSearch={(value) => setSearch(value.trim())}
              placeholder={text("Search sender, subject, content, person or contract")}
              style={{ flex: "1 1 360px", minWidth: 240 }}
            />
            <Select
              mode="multiple"
              allowClear
              value={filters.relevance ?? []}
              options={relevanceOptions}
              placeholder={text("All relevance levels")}
              onChange={(value: Array<InboundEmailRelevance | InboundEmailUnclassifiedFilterValue>) => setFilters((current) => ({
                ...current,
                relevance: value.length > 0 ? value : undefined,
              }))}
              style={{ minWidth: 170 }}
            />
            <Select
              mode="multiple"
              allowClear
              value={filters.caseType ?? []}
              options={caseTypeFilterOptions}
              placeholder={text("Request type")}
              onChange={(value: Array<InboundEmailCaseType | InboundEmailUnclassifiedFilterValue>) => setFilters((current) => ({
                ...current,
                caseType: value.length > 0 ? value : undefined,
              }))}
              style={{ minWidth: 180 }}
            />
            <Select
              mode="multiple"
              allowClear
              value={filters.status ?? []}
              options={statusOptions}
              placeholder={text("All processing statuses")}
              onChange={(value: InboundEmailStatus[]) => setFilters((current) => ({
                ...current,
                status: value.length > 0 ? value : undefined,
              }))}
              style={{ minWidth: 180 }}
            />
          </Flex>
        )}
      />

      <FormDrawer mode={(!canReview || !detail) ? "view" : "edit"}
        title="Email details"
        open={detailId !== null}
        onCancel={closeDetail}
        formId={CORRECTION_FORM_ID}
        submitDisabled={!correctionDirty || !canReview || !detail || detailLoading || detailLoadFailed}
        extra={emailHeaderItems.length > 0 ? <ActionMenu label={text("Actions")} disabled={correctionSubmitBlocked} items={emailHeaderItems}
          onAction={key => key === INBOUND_EMAIL_REANALYZE_ACTION ? reanalyze() : processEmailAction(key as EmailHeaderAction)} /> : undefined}
        dirty={canReview && correctionDirty} submitting={correctionSaving} hideSubmit={!canReview || !detail}
      >
        {!detailId ? null : detailLoading ? (
          <LoadingOverlay visible scope="placeholder" />
        ) : detailLoadFailed || !detail ? (
          <Alert
            type="error"
            showIcon
            message={text("Error loading data")}
            action={detailId ? (
              <Button size="small" onClick={() => loadDetail(detailId)}>
                {text("Try again")}
              </Button>
            ) : undefined}
          />
        ) : (
          <Form
            id={CORRECTION_FORM_ID}
            form={correctionForm}
            layout="vertical"
            onFinish={() => saveAndCloseDetail()}
            requiredMark={formDefaults.requiredMark}
          >
          <Tabs
            activeKey={detailTab}
            onChange={(key) => setDetailTab(key as EmailDetailTab)}
            items={[
              {
                key: "result",
                label: text("Result"),
                children: (
                  <Space orientation="vertical" size={spacing.xl} style={{ width: "100%" }}>
                    {detail.effectiveClassification?.summary ? (
                      <DetailList>
                        <DetailField label={text("Message goal")}>{detail.effectiveClassification.summary}</DetailField>
                        {extractedFieldOf(detail, "request_statement")?.value || extractedFieldOf(detail, "cancellation_statement")?.value ? (
                          <DetailField label={text("Request evidence")}>
                            {extractedFieldOf(detail, "request_statement")?.value ?? extractedFieldOf(detail, "cancellation_statement")?.value}
                          </DetailField>
                        ) : null}
                      </DetailList>
                    ) : null}
                    {detail.contractProcessings?.length ? <DetailList>
                      {detail.contractProcessings.map(processing => <DetailField key={processing.action}
                        label={text(processing.action === "cancellation" ? "Cancellation processed" : "Rest period processed")}>
                        {formatDateTimeNoSeconds(processing.completedAt, units)}
                      </DetailField>)}
                    </DetailList> : null}
                    {detail.processingError ? (
                      <Alert
                        type="error"
                        showIcon
                        message={text("Processing failed")}
                        description={(
                          <Space orientation="vertical" size={0}>
                            <Typography.Text>{reviewReasonLabel(detail.processingError, text)}</Typography.Text>
                            {detail.automaticRetryScheduled && detail.nextProcessingAttemptAt ? (
                              <Typography.Text type="secondary">
                                {text("Next automatic attempt")}:{" "}
                                {formatDateTimeNoSeconds(detail.nextProcessingAttemptAt, units)}
                              </Typography.Text>
                            ) : detail.automaticRetriesExhausted ? (
                              <Typography.Text type="secondary">
                                {text("The automatic analysis attempts are exhausted.")}
                              </Typography.Text>
                            ) : (
                              <Typography.Text type="secondary">
                                {text("No further automatic retry is scheduled.")}
                              </Typography.Text>
                            )}
                            {(detail.processingAttempts ?? 0) > 0 ? (
                              <Typography.Text type="secondary">
                                {text("Attempts")}: {detail.processingAttempts}
                              </Typography.Text>
                            ) : null}
                          </Space>
                        )}
                      />
                    ) : null}
                    {detail.reviewReason && !detail.processingError ? (
                      <Alert
                        type="warning"
                        showIcon
                        message={text("Review required")}
                        description={reviewReasonLabel(detail.reviewReason, text)}
                      />
                    ) : null}
                    {canReview ? (
                      <div>
                        <Form.Item name="relevance" label={text("Relevance")} rules={[{ required: true }]}>
                          <Select options={relevanceOptions.filter(option => option.value !== INBOUND_EMAIL_UNCLASSIFIED_FILTER_VALUE)}
                            onChange={(value) => {
                              if (value === INBOUND_EMAIL_RELEVANCE_VALUE.irrelevant) correctionForm.setFieldValue("intentTypes", []);
                            }} />
                        </Form.Item>
                        <Form.Item
                          name="intentTypes"
                          label={text("Core requests")}
                          hidden={correctionRelevance === "irrelevant"}
                          rules={[{ required: correctionRelevance === "relevant" }]}
                        >
                          <Select mode="multiple" options={manuallyActionableCaseTypeOptions} />
                        </Form.Item>
                    <Form.Item
                      name="personId"
                      label={text("Person")}
                      extra={personReferenceOf(detail)
                        ? `${text("Person reference in the message")}: ${personReferenceOf(detail)}`
                        : undefined}
                    >
                      <Select
                        allowClear
                        showSearch
                        filterOption={false}
                        options={personOptions}
                        onChange={(value) => {
                          if (value) return;
                          setContractChoices([]);
                          setContractOptions([]);
                          correctionForm.setFieldValue("contractPackageId", undefined);
                        }}
                        onSearch={searchPersons}
                        placeholder={text("Search person")}
                      />
                    </Form.Item>
                    <Form.Item name="contractPackageId" label={text("Contract")}>
                      <Select
                        allowClear
                        disabled={!correctionPersonId}
                        options={contractOptions}
                        placeholder={text("Select contract")}
                      />
                    </Form.Item>
                    {correctionIntentTypes.includes("cancellation") ? (
                      <>
                        <DetailList>
                          <DetailField label={text("Cancellation received")}>
                            {formatDateTimeNoSeconds(detail.receivedAt, units)}
                          </DetailField>
                          <DetailField label={text("Calculated cancellation date")}>
                            {detail.calculatedCancellationDate
                              ? formatDateOnly(detail.calculatedCancellationDate, units)
                              : text("—")}
                          </DetailField>
                        </DetailList>
                        <Form.Item name="requestedCancellationDate" label={text("Requested cancellation date")}>
                          <DatePicker style={{ width: "100%" }} format={dateInputFormats} />
                        </Form.Item>
                        <Form.Item name="cancellationReasonId" label={text("Cancellation reason")}>
                          <Select
                            allowClear
                            options={cancellationReasonOptions}
                            placeholder={text("Select cancellation reason…")}
                          />
                        </Form.Item>
                        <Form.Item name="note" label={text("Note")}>
                          <Input.TextArea rows={2} />
                        </Form.Item>
                      </>
                    ) : null}
                    {correctionIntentTypes.includes("rest_period") ? (
                      <>
                        <FormSectionHeading first title={text("Rest period")} sectionKey="rest-periods.details">
                          <Form.Item name="restPeriodReasonId" label={text("Rest period reason")}>
                            <Select
                              allowClear
                              options={restPeriodReasonOptions}
                              placeholder={text("Select rest period reason…")}
                            />
                          </Form.Item>
                          <div style={{ marginBottom: spacing.xl }}>
                          <ResponsiveFormGrid>
                            <Form.Item name="restPeriodStartDate" label={text("Start")}>
                              <DatePicker
                                style={{ width: "100%" }}
                                format={dateInputFormats}
                                disabledDate={disableFutureRestPeriodDate}
                              />
                            </Form.Item>
                            <Form.Item
                              name="restPeriodEndDate"
                              label={text("End")}
                              rules={[{
                                validator: (_, value) => {
                                  const start = correctionForm.getFieldValue("restPeriodStartDate");
                                  return !start || !value || !dayjs(value).isBefore(dayjs(start), "day")
                                    ? Promise.resolve()
                                    : Promise.reject(new Error(text("End date must be after start date")));
                                },
                              }]}
                            >
                              <DatePicker
                                style={{ width: "100%" }}
                                format={dateInputFormats}
                              />
                            </Form.Item>
                          </ResponsiveFormGrid>
                          </div>
                          <Form.Item name="restPeriodTemplateId" label={text("Rest period template")}>
                            <Select
                              allowClear
                              options={restPeriodTemplates.map((template) => ({
                                value: template.id,
                                label: template.isActive === false
                                  ? `${template.name} (${text("Inactive")})`
                                  : template.name,
                              }))}
                              placeholder={text("Select template or configure individually…")}
                              onChange={(value) => applyRestPeriodTemplate(value)}
                            />
                          </Form.Item>
                          <Form.Item name="note" label={text("Note")}>
                            <Input.TextArea rows={2} />
                          </Form.Item>
                        </FormSectionHeading>
                        {correctionRestPeriodTemplateId ? (
                          <RestPeriodFormFields
                            form={correctionForm}
                            mode="instance"
                            currencyCode={currencyCode}
                            decimalSeparator={decimalSeparator}
                            moneyPlaceholder={moneyPlaceholder}
                            units={units}
                            first={false}
                          />
                        ) : null}
                      </>
                    ) : null}
                      </div>
                    ) : (
                      <DetailList>
                        <DetailField label={text("Request type")}>
                          {detail.effectiveCaseType
                            ? caseTypeLabel(
                                detail.effectiveCaseType,
                                text,
                                extractedFieldOf(detail, "appointment_change_type")?.value,
                              )
                            : text("—")}
                        </DetailField>
                        <DetailField label={text("Person")}>{detail.personDisplayName || text("Not assigned")}</DetailField>
                        {personReferenceOf(detail) ? (
                          <DetailField label={text("Person reference in the message")}>
                            {personReferenceOf(detail)}
                          </DetailField>
                        ) : null}
                        <DetailField label={text("Contract")}>{detail.contractPackageDisplayName || text("Not assigned")}</DetailField>
                        {detail.effectiveCaseType === "cancellation" ? (
                          <>
                            <DetailField label={text("Requested cancellation date")}>
                              {detail.requestedCancellationDate ? formatDateOnly(detail.requestedCancellationDate, units) : text("—")}
                            </DetailField>
                            <DetailField label={text("Calculated cancellation date")}>
                              {detail.calculatedCancellationDate ? formatDateOnly(detail.calculatedCancellationDate, units) : text("—")}
                            </DetailField>
                            <DetailField label={text("Cancellation reason")}>
                              {detail.cancellationReason || text("—")}
                            </DetailField>
                          </>
                        ) : null}
                        {detail.effectiveCaseType === "rest_period" ? (
                          <>
                            <DetailField label={text("Rest period reason")}>{detail.restPeriodReason || text("—")}</DetailField>
                            <DetailField label={text("Start")}>
                              {detail.restPeriodStart ? formatDateOnly(detail.restPeriodStart, units) : text("—")}
                            </DetailField>
                            <DetailField label={text("End")}>
                              {detail.restPeriodEnd ? formatDateOnly(detail.restPeriodEnd, units) : text("—")}
                            </DetailField>
                          </>
                        ) : null}
                        {detail.note ? <DetailField label={text("Note")}>{detail.note}</DetailField> : null}
                      </DetailList>
                    )}
                  </Space>
                ),
              },
              {
                key: "decision",
                label: text("Classification"),
                children: detail.processingStatus === "queued"
                  || detail.processingStatus === "processing"
                  || detail.processingStatus === "failed" ? (
                  <DetailList>
                    <DetailField label={text("Processing status")}>
                      <Tag color={statusColor(detail.processingStatus)}>
                        {statusLabel(detail.processingStatus, text)}
                      </Tag>
                    </DetailField>
                  </DetailList>
                ) : (
                  <Space orientation="vertical" size={spacing.lg} style={{ width: "100%" }}>
                    <section>
                      <Space orientation="vertical" size={spacing.sm} style={{ width: "100%" }}>
                        <Typography.Title level={5} style={{ margin: 0 }}>
                          {text("Core requests")}
                        </Typography.Title>
                        {classifiedIntentsOf(detail).length > 0 ? (
                          <Space orientation="vertical" size={spacing.md} style={{ width: "100%" }}>
                            {classifiedIntentsOf(detail).map((intent) => (
                              <div key={intent.type}>
                                <Typography.Text strong>
                                  {caseTypeLabel(
                                    intent.type,
                                    text,
                                    extractedFieldOf(detail, "appointment_change_type")?.value,
                                  )}
                                </Typography.Text>
                                {intent.summary ? (
                                  <Typography.Paragraph style={{ marginBlock: spacing.micro }}>
                                    {intent.summary}
                                  </Typography.Paragraph>
                                ) : null}
                              </div>
                            ))}
                            <Typography.Text type="secondary">
                              {confidenceLabel(
                                detail.effectiveClassification?.confidence,
                                text("Assessment confidence"),
                                text,
                              )}
                            </Typography.Text>
                          </Space>
                        ) : (
                          <Space orientation="vertical" size={spacing.micro}>
                            <Typography.Text strong>
                              {(detail.effectiveRelevance ?? detail.effectiveClassification?.relevance)
                                === "irrelevant"
                                ? text("No business request recognized")
                                : text("No core request identified")}
                            </Typography.Text>
                            {detail.effectiveClassification?.summary ? (
                              <Typography.Text>{detail.effectiveClassification.summary}</Typography.Text>
                            ) : null}
                            <Typography.Text type="secondary">
                              {confidenceLabel(
                                detail.effectiveClassification?.confidence,
                                text("Assessment confidence"),
                                text,
                              )}
                            </Typography.Text>
                          </Space>
                        )}
                        {classificationReasonFieldOf(detail) ? (
                          <DetailList>
                            <DetailField label={text("Classification reason")}>
                              <Space orientation="vertical" size={spacing.micro}>
                                <Typography.Text>{classificationReasonFieldOf(detail)?.value}</Typography.Text>
                              </Space>
                            </DetailField>
                          </DetailList>
                        ) : null}
                      </Space>
                    </section>
                    {hasExtractedInformation(detail) ? (
                      <section>
                        <Space orientation="vertical" size={spacing.sm} style={{ width: "100%" }}>
                          <Typography.Title level={5} style={{ margin: 0 }}>
                            {text("Extracted information")}
                          </Typography.Title>
                          <DetailList>
                            {personReferenceFieldOf(detail) ? (
                              <DetailField label={text("Name in the message")}>
                                <Space orientation="vertical" size={spacing.micro}>
                                  <Typography.Text>{personReferenceFieldOf(detail)?.value}</Typography.Text>
                                </Space>
                              </DetailField>
                            ) : null}
                            {extractedFieldOf(detail, "date_of_birth") ? (
                              <DetailField label={text("Date of birth")}>
                                <Space orientation="vertical" size={spacing.micro}>
                                  <Typography.Text>
                                    {formatDateOnly(
                                      extractedFieldOf(detail, "date_of_birth")!.value!,
                                      units,
                                    )}
                                  </Typography.Text>
                                </Space>
                              </DetailField>
                            ) : null}
                            {extractedFieldOf(detail, "zip") ? (
                              <DetailField label={text("ZIP")}>
                                <Space orientation="vertical" size={spacing.micro}>
                                  <Typography.Text>{extractedFieldOf(detail, "zip")?.value}</Typography.Text>
                                </Space>
                              </DetailField>
                            ) : null}
                            {extractedFieldOf(detail, "city") ? (
                              <DetailField label={text("City")}>
                                <Space orientation="vertical" size={spacing.micro}>
                                  <Typography.Text>{extractedFieldOf(detail, "city")?.value}</Typography.Text>
                                </Space>
                              </DetailField>
                            ) : null}
                            {extractedFieldOf(detail, "phone") ? (
                              <DetailField label={text("Phone")}>
                                <Space orientation="vertical" size={spacing.micro}>
                                  <Typography.Text>{extractedFieldOf(detail, "phone")?.value}</Typography.Text>
                                </Space>
                              </DetailField>
                            ) : null}
                            {extractedFieldOf(detail, "cancellation_reason") ? (
                              <DetailField label={text("Cancellation reason")}>
                                <Space orientation="vertical" size={spacing.micro}>
                                  <Typography.Text>
                                    {extractedFieldOf(detail, "cancellation_reason")?.value}
                                  </Typography.Text>
                                </Space>
                              </DetailField>
                            ) : null}
                            {extractedFieldOf(detail, "rest_period_reason") ? (
                              <DetailField label={text("Rest period reason")}>
                                <Space orientation="vertical" size={spacing.micro}>
                                  <Typography.Text>
                                    {extractedFieldOf(detail, "rest_period_reason")?.value}
                                  </Typography.Text>
                                </Space>
                              </DetailField>
                            ) : null}
                            {extractedFieldOf(detail, "rest_period_request_type") ? (
                              <DetailField label={text("Rest period request type")}>
                                <Space orientation="vertical" size={spacing.micro}>
                                  <Typography.Text>
                                    {restPeriodRequestTypeLabel(
                                      extractedFieldOf(detail, "rest_period_request_type")?.value,
                                      text,
                                    )}
                                  </Typography.Text>
                                </Space>
                              </DetailField>
                            ) : null}
                            {/* Absage und Verschiebung stehen jetzt im Kernanliegen; hier bliebe nur
                                die Wiederholung. Der unklare Fall trägt dagegen eine eigene Aussage:
                                Das Modell konnte die Richtung nicht entscheiden. */}
                            {extractedFieldOf(detail, "appointment_change_type")?.value === APPOINTMENT_CHANGE_TYPE.unclear ? (
                              <DetailField label={text("Appointment change") }>
                                <Space orientation="vertical" size={spacing.micro}>
                                  <Typography.Text>
                                    {appointmentChangeTypeLabel(
                                      extractedFieldOf(detail, "appointment_change_type")?.value,
                                      text,
                                    )}
                                  </Typography.Text>
                                </Space>
                              </DetailField>
                            ) : null}
                            {extractedFieldOf(detail, "appointment_date") ? (
                              <DetailField label={text("Appointment date") }>
                                <Space orientation="vertical" size={spacing.micro}>
                                  <Typography.Text>
                                    {formatDateOnly(extractedFieldOf(detail, "appointment_date")!.value!, units)}
                                  </Typography.Text>
                                </Space>
                              </DetailField>
                            ) : null}
                            {extractedFieldOf(detail, "appointment_time") ? (
                              <DetailField label={text("Appointment time")}>
                                <Space orientation="vertical" size={spacing.micro}>
                                  <Typography.Text>{extractedFieldOf(detail, "appointment_time")?.value}</Typography.Text>
                                </Space>
                              </DetailField>
                            ) : null}
                            {detail.requestedCancellationDate ? (
                              <DetailField label={text("Requested cancellation date")}>
                                <Space orientation="vertical" size={spacing.micro}>
                                  <Typography.Text>
                                    {formatDateOnly(detail.requestedCancellationDate, units)}
                                  </Typography.Text>
                                </Space>
                              </DetailField>
                            ) : null}
                            {detail.restPeriodStart || detail.restPeriodEnd ? (
                              <DetailField label={text("Rest period")}>
                                <Space orientation="vertical" size={spacing.micro}>
                                  <Typography.Text>
                                    {[detail.restPeriodStart, detail.restPeriodEnd]
                                      .filter(Boolean)
                                      .map((date) => formatDateOnly(date!, units))
                                      .join(" – ")}
                                  </Typography.Text>
                                </Space>
                              </DetailField>
                            ) : null}
                          </DetailList>
                        </Space>
                      </section>
                    ) : null}
                    {intentTypesOf(detail).length > 0 ? (
                      <section>
                        <Space orientation="vertical" size={spacing.sm} style={{ width: "100%" }}>
                          <Typography.Title level={5} style={{ margin: 0 }}>
                            {text("Classification assignment")}
                          </Typography.Title>
                          <DetailList>
                            <DetailField label={text("Person")}>
                              {(detail.effectiveClassification?.personDisplayName
                                ?? detail.personDisplayName) ? (
                                <Space orientation="vertical" size={spacing.micro}>
                                  <Typography.Text strong>
                                    {detail.effectiveClassification?.personDisplayName
                                      ?? detail.personDisplayName}
                                  </Typography.Text>
                                  {(selectedPersonCandidateOf(detail)?.matchFactors ?? []).length > 0 ? (
                                    <Space size={spacing.xs} wrap>
                                      {(selectedPersonCandidateOf(detail)?.matchFactors ?? []).map((factor) => (
                                        <Tag key={factor}>{personMatchFactorLabel(factor, text)}</Tag>
                                      ))}
                                    </Space>
                                  ) : null}
                                  <Typography.Text type="secondary">
                                    {text("The person was matched uniquely.")}
                                  </Typography.Text>
                                </Space>
                              ) : (detail.personCandidates ?? []).length > 0 ? (
                                <Space orientation="vertical" size={spacing.sm}>
                                  {[...(detail.personCandidates ?? [])]
                                    .sort((left, right) =>
                                      (right.matchScore ?? 0) - (left.matchScore ?? 0))
                                    .map((candidate) => (
                                      <Space
                                        key={candidate.candidateId}
                                        orientation="vertical"
                                        size={spacing.micro}
                                      >
                                        <Typography.Text strong>
                                          {candidate.displayName}
                                        </Typography.Text>
                                        <Space size={spacing.xs} wrap>
                                          {(candidate.matchFactors ?? []).map((factor) => (
                                            <Tag key={factor}>
                                              {personMatchFactorLabel(factor, text)}
                                            </Tag>
                                          ))}
                                          {/* Nur Vorschlag: übernommen wird ins Korrekturfeld, gespeichert erst mit der Prüfung. */}
                                          {canReview && candidate.id && correctionPersonId !== candidate.id ? (
                                            <Button size="small" type="link"
                                              onClick={() => correctionForm.setFieldsValue({ personId: candidate.id!, contractPackageId: undefined })}>
                                              {text("Apply")}
                                            </Button>
                                          ) : null}
                                        </Space>
                                      </Space>
                                    ))}
                                  <Typography.Text type="secondary">
                                    {text("The assignment is ambiguous and needs review.")}
                                  </Typography.Text>
                                </Space>
                              ) : (
                                <Typography.Text type="secondary">
                                  {text("No person candidates found.")}
                                </Typography.Text>
                              )}
                            </DetailField>
                            {intentTypesOf(detail).includes("appointment_change") ? (
                              <DetailField label={text("Appointment candidates")}>
                                {(detail.appointmentCandidates ?? []).length > 0 ? (
                                  <Space orientation="vertical" size={spacing.sm}>
                                    {(detail.appointmentCandidates ?? []).map((candidate) => (
                                      <Space key={candidate.id} orientation="vertical" size={spacing.micro}>
                                        <Typography.Text strong>
                                          {candidate.appointmentType || text("Appointment")}
                                          {candidate.selected ? ` · ${text("uniquely matched")}` : ""}
                                        </Typography.Text>
                                        <Typography.Text>
                                          {formatDateTimeNoSeconds(candidate.startTime, units)}
                                        </Typography.Text>
                                      </Space>
                                    ))}
                                  </Space>
                                ) : (
                                  <Typography.Text type="secondary">
                                    {text("No matching appointment was found.")}
                                  </Typography.Text>
                                )}
                              </DetailField>
                            ) : null}
                            {hasContractIntent(detail) ? (
                              <DetailField label={text("Contract")}>
                                {(detail.effectiveClassification?.contractPackageDisplayName
                                  ?? detail.contractPackageDisplayName) ? (
                                  <Space orientation="vertical" size={spacing.micro}>
                                    <Typography.Text strong>
                                      {detail.effectiveClassification?.contractPackageDisplayName
                                        ?? detail.contractPackageDisplayName}
                                      {selectedContractCandidateOf(detail)?.status
                                        ? ` · ${contractStatusLabel(
                                            selectedContractCandidateOf(detail)?.status,
                                            text,
                                          )}`
                                        : ""}
                                    </Typography.Text>
                                    {(selectedContractCandidateOf(detail)?.selectionFactors ?? [])
                                      .length > 0 ? (
                                        <Space size={spacing.xs} wrap>
                                          {(selectedContractCandidateOf(detail)?.selectionFactors ?? [])
                                            .map((factor) => (
                                              <Tag key={factor}>
                                                {contractSelectionFactorLabel(factor, text)}
                                              </Tag>
                                            ))}
                                        </Space>
                                      ) : null}
                                  </Space>
                                ) : (detail.contractCandidates ?? []).length > 0 ? (
                                  <Space orientation="vertical" size={spacing.sm}>
                                    {[...(detail.contractCandidates ?? [])]
                                      .sort((left, right) =>
                                        (right.selectionScore ?? Number.MIN_SAFE_INTEGER)
                                          - (left.selectionScore ?? Number.MIN_SAFE_INTEGER)
                                        || (right.selectionPreferenceCount ?? 0)
                                          - (left.selectionPreferenceCount ?? 0))
                                      .map((candidate) => (
                                        <Space
                                          key={candidate.candidateId}
                                          orientation="vertical"
                                          size={spacing.micro}
                                        >
                                          <Typography.Text strong>
                                            {candidate.displayName}
                                            {" · "}
                                            {contractStatusLabel(candidate.status, text)}
                                          </Typography.Text>
                                          <Space size={spacing.xs} wrap>
                                            {(candidate.selectionFactors ?? []).map((factor) => (
                                              <Tag key={factor}>
                                                {contractSelectionFactorLabel(factor, text)}
                                              </Tag>
                                            ))}
                                          </Space>
                                        </Space>
                                      ))}
                                  </Space>
                                ) : (
                                  <Typography.Text type="secondary">
                                    {(detail.effectiveClassification?.personId ?? detail.personId)
                                      ? text("No contracts found")
                                      : text(
                                          "Without an assigned person there are no contract candidates.",
                                        )}
                                  </Typography.Text>
                                )}
                              </DetailField>
                            ) : null}
                          </DetailList>
                        </Space>
                      </section>
                    ) : null}
                    {(detail.effectiveClassification?.uncertainties ?? []).length > 0 ? (
                      <section>
                        <Space orientation="vertical" size={spacing.sm} style={{ width: "100%" }}>
                          <Typography.Title level={5} style={{ margin: 0 }}>
                            {text("Open points")}
                          </Typography.Title>
                          <Space orientation="vertical" size={spacing.xs}>
                            {(detail.effectiveClassification?.uncertainties ?? []).map((uncertainty) => (
                              <Typography.Text key={uncertainty}>{uncertainty}</Typography.Text>
                            ))}
                          </Space>
                        </Space>
                      </section>
                    ) : null}
                  </Space>
                ),
              },
              {
                key: "message",
                label: text("Original message"),
                children: (
                  <Space orientation="vertical" size={spacing.xl} style={{ width: "100%" }}>
                    <section>
                      <DetailList>
                        <DetailField label={text("Subject")}>{detail.subject || text("(no subject)")}</DetailField>
                        <DetailField label={text("From")}>
                          {detail.fromDisplayName ? `${detail.fromDisplayName} <${detail.fromEmail}>` : detail.fromEmail}
                        </DetailField>
                        <DetailField label={text("To")}>{detail.toEmail || text("—")}</DetailField>
                        <DetailField label={text("Received at")}>{formatDateTimeNoSeconds(detail.receivedAt, units)}</DetailField>
                        <DetailField label={text("Mailbox")}>{detail.accountName || text("—")}</DetailField>
                      </DetailList>
                    </section>
                    <section>
                      {detail.bodyHtml ? (
                        <div
                          style={{ overflowWrap: "anywhere" }}
                          dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(detail.bodyHtml) }}
                        />
                      ) : (
                        <Typography.Paragraph style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", marginBottom: 0 }}>
                          {detail.bodyText || text("No content.")}
                        </Typography.Paragraph>
                      )}
                    </section>
                    <section>
                      <Typography.Text type="secondary">{text("Attachments")}</Typography.Text>
                      {(detail.attachments ?? []).length === 0 ? (
                        <Typography.Text type="secondary">{text("No attachments")}</Typography.Text>
                      ) : (
                        <Space orientation="vertical" size={spacing.sm} style={{ width: "100%" }}>
                          {(detail.attachments ?? []).map((attachment) => (
                            <Flex key={attachment.id} align="center" justify="space-between" gap={spacing.sm} wrap="wrap">
                              <div style={{ minWidth: 0 }}>
                                <Typography.Text>{attachment.fileName}</Typography.Text>
                                <br />
                                <Typography.Text type="secondary">
                                  {extractionStatusLabel(attachment.extractionStatus, text)}
                                  {attachment.errorReason ? ` · ${reviewReasonLabel(attachment.errorReason, text)}` : ""}
                                </Typography.Text>
                              </div>
                              <Button type="link" icon={<DownloadOutlined />} onClick={() => downloadAttachment(attachment)}>
                                {text("Download")}
                              </Button>
                            </Flex>
                          ))}
                        </Space>
                      )}
                    </section>
                  </Space>
                ),
              },
            ]}
          />
          </Form>
        )}
      </FormDrawer>
    </>
  </React.Activity></>
    );
}
